/**
 * Dropdown Menu component
 * Brutalist-styled wrapper around Radix UI Dropdown Menu
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ReactNode } from 'react';

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
}

interface DropdownItemProps {
  children: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  className?: string;
}

interface DropdownSeparatorProps {
  className?: string;
}

interface DropdownLabelProps {
  children: ReactNode;
  className?: string;
}

/**
 * Main dropdown component
 */
export function Dropdown({ trigger, children, align = 'end', sideOffset = 8 }: DropdownProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="
            min-w-[220px]
            bg-white dark:bg-gray-900
            border-2 border-black dark:border-white
            rounded-lg
            shadow-brutal
            p-1
            z-50
            animate-in fade-in-0 zoom-in-95
            data-[side=top]:slide-in-from-bottom-2
            data-[side=bottom]:slide-in-from-top-2
            data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2
          "
          align={align}
          sideOffset={sideOffset}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Dropdown menu item
 */
export function DropdownItem({ children, onSelect, disabled, destructive, className = '' }: DropdownItemProps) {
  return (
    <DropdownMenu.Item
      className={`
        relative flex items-center
        px-3 py-2 my-1
        rounded-md
        outline-none
        cursor-pointer
        select-none
        text-sm
        transition-colors

        ${
          destructive
            ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
            : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
        }

        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}

        focus:bg-gray-100 dark:focus:bg-gray-800

        ${className}
      `}
      onSelect={onSelect}
      disabled={disabled}
    >
      {children}
    </DropdownMenu.Item>
  );
}

/**
 * Dropdown separator
 */
export function DropdownSeparator({ className = '' }: DropdownSeparatorProps) {
  return (
    <DropdownMenu.Separator
      className={`
        h-px my-1
        bg-gray-200 dark:bg-gray-700
        ${className}
      `}
    />
  );
}

/**
 * Dropdown label (non-interactive)
 */
export function DropdownLabel({ children, className = '' }: DropdownLabelProps) {
  return (
    <DropdownMenu.Label
      className={`
        px-3 py-2
        text-xs font-semibold
        text-gray-500 dark:text-gray-400
        uppercase tracking-wide
        ${className}
      `}
    >
      {children}
    </DropdownMenu.Label>
  );
}

// Export individual components
export const DropdownTrigger = DropdownMenu.Trigger;
export const DropdownContent = DropdownMenu.Content;
export const DropdownGroup = DropdownMenu.Group;
